// Sticky filter bar for the permission catalogue: a single-select area chip row
// (Tous + one chip per area that actually has permissions) plus an accent- and
// case-insensitive text search. Used on the Permissions screen AND inside the
// role-permission drawer so both surfaces behave identically. The pure narrowing
// lives in lib/grouping (`filterPermissions`); this file is just the UI plus a
// small state hook that wires it up the same way in both places.

import { useMemo, useState } from "react"
import { fr } from "../i18n/fr.js"
import { type AreaGroup, areaLabel, filterPermissions } from "../lib/grouping.js"
import { SearchIcon } from "../ui/icons.js"

export interface PermissionFilterState {
  /** Selected area key, or null for "all areas". */
  area: string | null
  setArea: (area: string | null) => void
  query: string
  setQuery: (query: string) => void
  /** Area keys (rank-ordered) that have permissions — the chip set. */
  areaKeys: string[]
  /** The narrowed Area → Domain tree. */
  filtered: AreaGroup[]
  /** True while any filter (area or query) is active. */
  active: boolean
  /** True while a text query is present — used to auto-expand accordions. */
  searchActive: boolean
}

/**
 * State + memoized narrowing for the permission filter. Filtering NEVER touches
 * any external selection (the drawer keeps its pending selection as a Set of
 * permission ids, independent of what the tree renders), so narrowing the view
 * can never drop or reset a pending toggle.
 */
export function usePermissionFilter(groups: AreaGroup[]): PermissionFilterState {
  const [area, setArea] = useState<string | null>(null)
  const [query, setQuery] = useState("")

  const areaKeys = useMemo(() => groups.map((g) => g.area), [groups])
  // An area that vanishes from the catalogue must not stay selected.
  const effectiveArea = area !== null && areaKeys.includes(area) ? area : null

  const filtered = useMemo(
    () =>
      filterPermissions(groups, {
        area: effectiveArea,
        query,
        domainDict: fr.permissions.domain
      }),
    [groups, effectiveArea, query]
  )

  const searchActive = query.trim().length > 0
  return {
    area: effectiveArea,
    setArea,
    query,
    setQuery,
    areaKeys,
    filtered,
    active: effectiveArea !== null || searchActive,
    searchActive
  }
}

export function PermissionFilterBar({
  areaKeys,
  area,
  onAreaChange,
  query,
  onQueryChange,
  onSurface = false
}: {
  areaKeys: string[]
  area: string | null
  onAreaChange: (area: string | null) => void
  query: string
  onQueryChange: (query: string) => void
  /** Render on a surface (drawer) background rather than the page background. */
  onSurface?: boolean
}) {
  const className = ["filter-bar", onSurface ? "filter-bar--on-surface" : ""]
    .filter(Boolean)
    .join(" ")
  return (
    <div className={className}>
      <fieldset className="filter-bar__chips" aria-label={fr.permissions.filter.areaLabel}>
        <button
          type="button"
          className={["chip", "chip--selectable", area === null ? "chip--selected" : ""]
            .filter(Boolean)
            .join(" ")}
          aria-pressed={area === null}
          onClick={() => onAreaChange(null)}
        >
          {fr.permissions.filter.all}
        </button>
        {areaKeys.map((key) => (
          <button
            key={key}
            type="button"
            className={["chip", "chip--selectable", area === key ? "chip--selected" : ""]
              .filter(Boolean)
              .join(" ")}
            aria-pressed={area === key}
            onClick={() => onAreaChange(area === key ? null : key)}
          >
            {areaLabel(key, fr.permissions.area)}
          </button>
        ))}
      </fieldset>
      <div className="filter-bar__search-wrap">
        <span className="filter-bar__search-icon" aria-hidden="true">
          <SearchIcon />
        </span>
        <input
          type="search"
          className="field__input filter-bar__search"
          aria-label={fr.permissions.filter.searchPlaceholder}
          placeholder={fr.permissions.filter.searchPlaceholder}
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
        />
      </div>
    </div>
  )
}
